<template>
    <div>
        <h1 class="page-header">{{ $t('str.msg.dashboard.performance.title') }}</h1>
        <hr />

        <div class="row mb-4">
            <div class="col-md-2">
                <label class="form-label" for="accountField">{{ $t('str.register.period') }}</label>
                <date-range-picker
                    ref="picker"
                    class="form-select btn btn-dark me-2"
                    :opens="dateRange.opens"
                    :locale-data="dateRange.sampleLocaleData"
                    :locale="dateRange.locale"
                    :singleDatePicker="dateRange.singleDatePicker"
                    :timePicker="dateRange.timePicker"
                    :timePicker24Hour="dateRange.timePicker24Hour"
                    :showWeekNumbers="dateRange.showWeekNumbers"
                    :showDropdowns="dateRange.showDropdowns"
                    v-model="dateRange.range"
                    :max-date="dateRange.maxDate"
                    :ranges="dateRanges"
                    :linkedCalendars="dateRange.linkedCalendars"
                    @update="updateValues"
                    @start-selection="onStartSelection"
                    @finish-selection="onFinishSelection"
                >
                    <template v-slot:input="filters">
                        <i class="fa fa-calendar fa-fw text-white text-opacity-50 ms-n1"></i>
                        {{ formatDate(filters.startDate) }} - {{ formatDate(filters.endDate) }}
                        <b class="caret ms-1 opacity-5"></b>
                    </template>
                </date-range-picker>
            </div>
            <div v-if="isSuperAdminMaster" class="col-md-3">
                <label class="form-label" for="accountField">{{ $t('str.register.incident.account.field') }}</label>
                <select v-model="filters.account" @change="changeAccount" class="form-select" id="accountField">
                    <option value="">{{ $t('str.register.select.placeholder') }}</option>
                    <option v-for="account in accounts" :value="account._id" :key="account._id">
                        {{ account.name }}
                    </option>
                </select>
            </div>
            <div v-if="role === 'SUPER_ADMIN_MASTER' || role === 'ADMIN' || role === 'MANAGER'" class="col-md-3">
                <label class="form-label" for="clientField">{{ $t('str.register.incident.client.field') }}</label>
                <select v-model="filters.client" @change="changeClient" class="form-select" id="clientField">
                    <option value="">{{ $t('str.register.select.placeholder') }}</option>
                    <option v-for="client in clients" :value="client._id" :key="client._id">
                        {{ client.name }}
                    </option>
                </select>
            </div>
            <div class="col-md-3">
                <label class="form-label" for="siteField">{{ $t('str.register.incident.site.field') }}</label>
                <select v-model="filters.site" @change="changeSite" class="form-select" id="siteField">
                    <option value="">{{ $t('str.register.select.placeholder') }}</option>
                    <option v-for="site in sites" :value="site._id" :key="site._id">
                        {{ site.name }}
                    </option>
                </select>
            </div>
            <div class="col-md-1 text-center align-self-end">
                <button @click="filter" type="submit" class="btn btn-primary is-loading">
                    <i v-if="isSearchLoading" class="fas fa-spinner fa-pulse"></i>
                    {{ $t('str.btn.search') }}
                </button>
            </div>
        </div>

        <div class="row">
            <div class="col-sm-12 col-lg-6">
                <LocalsPerformance :title="$t('str.chart.locals.performance.title')" :data="localsPerformance" :locale="userLocale" />
            </div>
            <div class="col-sm-12 col-lg-6">
                <GuardsPerformance :title="$t('str.chart.guards.performance.title')" :data="guardsPerformance" :locale="userLocale" />
            </div>
        </div>

        <div v-if="isLoading" class="spinner-overlay">
            <b-spinner label="Carregando..." style="width: 2rem; height: 2rem" type="border" variant="primary" />
        </div>

        <notifications group="top-right" position="top right" :speed="1000" />
    </div>
</template>

<script>
import Vue from 'vue'

import GuardsPerformance from './Components/GuardsPerformance.vue'
import LocalsPerformance from './Components/LocalsPerformance.vue'
import Controller from './CrtPerformance.vue'
import moment from 'moment-timezone'
import Common from '../../../common/Common.vue'

Vue.prototype.$registerEvent = new Vue()

export default {
    components: {
        GuardsPerformance,
        LocalsPerformance,
    },
    data() {
        const accountTimezone = Common.getAccountTimezone()
        return {
            isLoading: false,
            dateRange: {},
            role: '',
            filters: {
                account: '',
                client: '',
                site: '',
                startDate: moment().tz(accountTimezone),
                endDate: moment().tz(accountTimezone),
            },
            userLocale: 'pt-br',
            accounts: [],
            clients: [],
            sites: [],
            isSuperAdminMaster: false,
            localsPerformance: [],
            guardsPerformance: [],
            dateRanges: {
                [this.$t('str.date.today')]: [moment().tz(accountTimezone).toDate(), moment().tz(accountTimezone).toDate()],
                [this.$t('str.date.yesterday')]: [moment().tz(accountTimezone).subtract(1, 'days').toDate(), moment().tz(accountTimezone).subtract(1, 'days').toDate()],
                [this.$t('str.date.this.month')]: [moment().tz(accountTimezone).startOf('month').toDate(), moment().tz(accountTimezone).toDate()],
                [this.$t('str.date.last.month')]: [moment().tz(accountTimezone).subtract(1, 'month').startOf('month').toDate(), moment().tz(accountTimezone).subtract(1, 'month').endOf('month').toDate()],
            },
            tableTitle: this.$t('str.dashboard.events.by.type'),
            tableHeaders: [this.$t('str.column.type'), this.$t('str.column.total')],
        }
    },
    methods: Controller.methods,
    created() {
        Controller.init(this)

        const state = this
        state.$registerEvent.$on('changeLanguage', function () {
            state.initRangeDate()
            state.userLocale = state.$i18n.locale === 'pt' ? 'pt-br' : 'en'
        })
    },
    computed: {
        isDateRangeValid() {
            const { startDate, endDate } = this.dateRange.range
            return moment(endDate).diff(moment(startDate), 'days') <= 30
        },
    },
}
</script>
<style lang="scss" scoped>
.vue-daterange-picker.btn {
    padding: 2px !important;
    display: flex !important;
    background-color: var(--app-component-bg) !important;
    text-align-last: start !important;
}
.caret {
    display: none !important;
}
.spinner-overlay {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
}
.graph-container {
    height: calc(100vh - 200px); /* Ajusta dinamicamente a altura para ocupar a viewport */
    overflow-y: auto; /* Evita estouro */
}
@media (max-width: 768px) {
    .page-header {
        font-size: 1.5rem;
    }
    .form-label {
        font-size: 0.875rem;
    }
    .form-select {
        padding: 0.25rem;
    }
    .mb-4 {
        margin-bottom: 1rem !important;
    }
}
</style>
